// client/src/components/ContentGraphDashboard.js
// Rebuilt Jun 2026 — Content Health / Visibility styling
// Prompt table with drawer (replaces modal): Matched / Missing / Priority tabs
// Columns: Prompt | Matched % | Priority Entities (count) | Health Score

import React, { useState, useEffect } from 'react';

if (typeof document !== 'undefined') {
  if (!document.querySelector('link[href*="tabler-icons"]')) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/tabler-icons.min.css';
    document.head.appendChild(link);
  }
  if (!document.querySelector('link[href*="Work+Sans"]')) {
    const fl = document.createElement('link');
    fl.href = 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700;800&display=swap';
    fl.rel = 'stylesheet';
    document.head.appendChild(fl);
  }
}

const colors = {
  primary: '#39393a',
  secondary: '#048ba8',
  tertiary: '#e9c46a',
  accent: '#f4a261',
  grey: '#e76f51',
  green: '#01796f'
};

// Competitor filter (preserved from original)
const COMPETITOR_KEYWORDS = [
  'brooks brothers','suit supply','suitsupply','nordstrom','indochino','black lapel',
  'macys',"macy's",'savile row tailors','h huntsman','huntsman','gieves','hawkes',
  'henry poole','anderson','sheppard','tom ford','reiss','charles tyrwhitt','hockerty',
  'edit suits','studiosuits','studio suits','tailor store','tm lewin','t.m. lewin',
  't m lewin','savile row company','harrods','angloitalian','anglo italian','husbands',
  'samanamel','stoffa','thom sweeney'
];
const isCompetitor = (name) => {
  if (!name) return false;
  const n = name.toLowerCase().trim();
  return COMPETITOR_KEYWORDS.some(k => n.includes(k) || k.includes(n));
};

function band(score) {
  if (score === null || score === undefined) return { bg: '#f3f4f6', text: '#9ca3af', bar: '#d1d5db' };
  if (score >= 70) return { bg: 'rgba(1,121,111,0.08)', text: '#01796f', bar: '#01796f' };
  if (score >= 45) return { bg: 'rgba(233,196,106,0.15)', text: '#854F0B', bar: '#e9c46a' };
  return { bg: 'rgba(231,111,81,0.08)', text: '#993C1D', bar: '#e76f51' };
}

function ScoreCell({ score }) {
  const b = band(score);
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '40px', padding: '3px 8px', borderRadius: '6px', backgroundColor: b.bg, color: b.text, fontSize: '13px', fontWeight: '600' }}>
      {score === null || score === undefined ? '—' : `${score}%`}
    </div>
  );
}

function formatUrl(url) {
  try {
    const u = new URL(url);
    return u.pathname === '/' ? u.hostname : u.pathname;
  } catch { return url?.substring(0, 40) || ''; }
}

// ---- Drawer ----
function Drawer({ open, onClose, title, badge, badgeBg, badgeColor, children }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1000 }} />
      <div role="dialog" style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(640px, 92vw)', backgroundColor: 'white', boxShadow: '-8px 0 24px rgba(0,0,0,0.18)', zIndex: 1001, display: 'flex', flexDirection: 'column', fontFamily: "'Work Sans', sans-serif" }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexShrink: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '15px', fontWeight: '700', color: colors.primary, lineHeight: 1.4 }}>{title}</span>
              {badge && <span style={{ padding: '3px 10px', borderRadius: '8px', backgroundColor: badgeBg, color: badgeColor, fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap' }}>{badge}</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1, flexShrink: 0 }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px' }}>
          {children}
        </div>
      </div>
    </>
  );
}

// ---- Entity drawer body ----
function EntityDrawerBody({ row }) {
  const [tab, setTab] = useState('priority');
  const [showAll, setShowAll] = useState(false);

  const tabs = [
    { id: 'priority', label: `Priority (${row.entities.priority.length})`, color: colors.accent },
    { id: 'matched', label: `Matched (${row.entities.matched.length})`, color: colors.green },
    { id: 'missing', label: `Missing (${row.entities.missing.length})`, color: colors.grey },
  ];

  const getSalienceColor = (val) => {
    const v = parseFloat(val);
    if (v >= 0.5) return colors.secondary;
    if (v >= 0.3) return colors.tertiary;
    if (v >= 0.1) return colors.accent;
    return colors.grey;
  };

  // Mini scorecards
  const b = band(row.healthScore);

  return (
    <>
      {/* Mini stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '20px' }}>
        {[
          { label: 'Matched', value: `${row.matchedPct}%`, color: row.matchedPct >= 70 ? colors.green : row.matchedPct >= 45 ? '#854F0B' : '#993C1D', bg: band(row.matchedPct).bg },
          { label: 'Missing', value: `${row.missingPct}%`, color: row.missingPct <= 30 ? colors.green : row.missingPct <= 55 ? '#854F0B' : '#993C1D', bg: band(100 - row.missingPct).bg },
          { label: 'Health', value: `${row.healthScore}`, color: b.text, bg: b.bg },
        ].map(s => (
          <div key={s.label} style={{ padding: '10px', borderRadius: '8px', backgroundColor: s.bg, textAlign: 'center' }}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{s.label}</div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #e5e7eb', marginBottom: '16px' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setShowAll(false); }}
            style={{ padding: '8px 16px', fontSize: '13px', fontWeight: '600', background: 'none', border: 'none', borderBottom: tab === t.id ? `2px solid ${t.color}` : '2px solid transparent', marginBottom: '-2px', color: tab === t.id ? t.color : '#6b7280', cursor: 'pointer' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Priority tab */}
      {tab === 'priority' && (
        <div>
          <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '12px' }}>Entities with LLM salience ≥ 0.05, sorted by importance. Green = covered on your site, coral = gap.</p>
          {row.entities.priority.length === 0
            ? <p style={{ fontSize: '13px', color: '#6b7280', textAlign: 'center', padding: '24px 0' }}>No priority entities meet the threshold.</p>
            : <>
              {(showAll ? row.entities.priority : row.entities.priority.slice(0, 15)).map((e, i) => (
                <div key={i} style={{ padding: '10px 12px', borderRadius: '8px', border: `1px solid ${e.isMatched ? colors.green : colors.grey}20`, backgroundColor: e.isMatched ? 'rgba(1,121,111,0.04)' : 'rgba(231,111,81,0.04)', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <i className={`ti ${e.isMatched ? 'ti-check' : 'ti-x'}`} style={{ fontSize: '14px', color: e.isMatched ? colors.green : colors.grey, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary }}>{e.entity}</div>
                    {e.url && <div style={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'ui-monospace, monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatUrl(e.url)}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '4px', backgroundColor: `${getSalienceColor(e.llmSalience)}20`, color: getSalienceColor(e.llmSalience), fontWeight: '600' }}>{e.llmSalience}</span>
                    <span style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase' }}>{e.classification}</span>
                  </div>
                </div>
              ))}
              {row.entities.priority.length > 15 && !showAll && (
                <button onClick={() => setShowAll(true)} style={{ fontSize: '13px', color: colors.accent, fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer', marginTop: '8px' }}>
                  View all {row.entities.priority.length} priority entities →
                </button>
              )}
            </>
          }
        </div>
      )}

      {/* Matched tab */}
      {tab === 'matched' && (
        <div>
          {row.entities.matched.length === 0
            ? <p style={{ fontSize: '13px', color: '#6b7280', textAlign: 'center', padding: '24px 0' }}>No matched entities for this prompt.</p>
            : <>
              {(showAll ? row.entities.matched : row.entities.matched.slice(0, 15)).map((e, i) => (
                <div key={i} style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(1,121,111,0.2)', backgroundColor: 'rgba(1,121,111,0.04)', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <i className="ti ti-check" style={{ fontSize: '14px', color: colors.green, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary }}>{e.entity}</div>
                    {e.url && <div style={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'ui-monospace, monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatUrl(e.url)}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '4px', backgroundColor: `${getSalienceColor(e.llmSalience)}20`, color: getSalienceColor(e.llmSalience), fontWeight: '600' }}>{e.llmSalience}</span>
                    <span style={{ fontSize: '11px', color: parseInt(e.diff) >= 0 ? colors.green : colors.grey, fontWeight: '600' }}>{e.diffLabel}</span>
                  </div>
                </div>
              ))}
              {row.entities.matched.length > 15 && !showAll && (
                <button onClick={() => setShowAll(true)} style={{ fontSize: '13px', color: colors.green, fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer', marginTop: '8px' }}>
                  View all {row.entities.matched.length} matched entities →
                </button>
              )}
            </>
          }
        </div>
      )}

      {/* Missing tab */}
      {tab === 'missing' && (
        <div>
          {row.entities.missing.length === 0
            ? <p style={{ fontSize: '13px', color: '#6b7280', textAlign: 'center', padding: '24px 0' }}>No missing entities — great coverage!</p>
            : <>
              {(showAll ? row.entities.missing : row.entities.missing.slice(0, 15)).map((e, i) => (
                <div key={i} style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(231,111,81,0.2)', backgroundColor: 'rgba(231,111,81,0.04)', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <i className="ti ti-x" style={{ fontSize: '14px', color: colors.grey, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary }}>{e.entity}</div>
                    {e.url && <div style={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'ui-monospace, monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatUrl(e.url)}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '4px', backgroundColor: `${getSalienceColor(e.llmSalience)}20`, color: getSalienceColor(e.llmSalience), fontWeight: '600' }}>{e.llmSalience}</span>
                    <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: '700', backgroundColor: e.priority === 'HIGH' ? '#fee2e2' : e.priority === 'MEDIUM' ? '#fef3c7' : '#fef9ee', color: e.priority === 'HIGH' ? '#dc2626' : e.priority === 'MEDIUM' ? '#854F0B' : '#9ca3af' }}>{e.priority}</span>
                  </div>
                </div>
              ))}
              {row.entities.missing.length > 15 && !showAll && (
                <button onClick={() => setShowAll(true)} style={{ fontSize: '13px', color: colors.grey, fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer', marginTop: '8px' }}>
                  View all {row.entities.missing.length} missing entities →
                </button>
              )}
            </>
          }
        </div>
      )}
    </>
  );
}

// ---- Main ----
export default function ContentGraphDashboard({ campaign, campaignId, urls, apiBaseUrl }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [drawer, setDrawer] = useState(null);
  const [sortColumn, setSortColumn] = useState('healthScore');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => { if (campaignId) load(); }, [campaignId]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBaseUrl}/audit-results?campaignId=${campaignId}&moduleType=content-graph&limit=100`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const results = await res.json();
      if (!results || results.length === 0) { setTableData([]); setLoading(false); return; }

      const byPrompt = {};
      results.forEach(r => {
        const rd = typeof r.result_data === 'string' ? JSON.parse(r.result_data) : r.result_data;
        const pt = rd?.metadata?.promptText || rd?.prompt || 'Unknown prompt';
        if (!byPrompt[pt] || new Date(r.created_at) > new Date(byPrompt[pt].created_at)) {
          byPrompt[pt] = { rd, created_at: r.created_at };
        }
      });

      const processed = Object.values(byPrompt).map(({ rd, created_at }) => processResult(rd, created_at)).filter(Boolean);
      setTableData(processed);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const processResult = (rd, createdAt) => {
    const prompt = rd?.metadata?.promptText || rd?.prompt || 'Unknown prompt';
    const isV48 = rd?.gaps?.details && !rd?.gapMatrix;
    const entities = isV48 ? processV48(rd) : processLegacy(rd);
    if (!entities || entities.length === 0) return null;

    const matched = entities.filter(e => e._isMatched);
    const missing = entities.filter(e => !e._isMatched);
    const matchedPct = Math.round((matched.length / entities.length) * 100);
    const missingPct = Math.round((missing.length / entities.length) * 100);

    const yourAvgSal = matched.length > 0 ? (matched.reduce((s, e) => s + (e._yourSalience || 0), 0) / matched.length).toFixed(4) : '0.0000';
    const llmAvgSal = entities.length > 0 ? (entities.reduce((s, e) => s + (e._llmSalience || 0), 0) / entities.length).toFixed(4) : '0.0000';

    const emphasisScore = parseFloat(llmAvgSal) > 0 ? Math.min(100, (parseFloat(yourAvgSal) / parseFloat(llmAvgSal)) * 100) : 0;
    const healthScore = Math.round((matchedPct * 0.4) + (emphasisScore * 0.3) + ((100 - missingPct) * 0.3));

    const matchedEntities = matched.map(e => {
      const diff = e._llmSalience > 0 ? Math.round(((e._yourSalience - e._llmSalience) / e._llmSalience) * 100) : 0;
      return { entity: e._entityName, classification: e._entityType || 'OTHER', url: e._bestURL || '', yourSalience: (e._yourSalience || 0).toFixed(4), llmSalience: (e._llmSalience || 0).toFixed(4), diff, diffLabel: diff >= 0 ? `+${diff}%` : `${diff}%` };
    }).sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));

    const missingEntities = missing.map(e => ({
      entity: e._entityName, classification: e._entityType || 'OTHER', url: e._bestURL || '',
      llmSalience: (e._llmSalience || 0).toFixed(4),
      priority: e._llmSalience >= 0.7 ? 'HIGH' : e._llmSalience >= 0.4 ? 'MEDIUM' : 'LOW'
    })).sort((a, b) => parseFloat(b.llmSalience) - parseFloat(a.llmSalience));

    const PRIORITY_THRESHOLD = 0.05;
    const priorityEntities = entities
      .filter(e => (e._llmSalience || 0) >= PRIORITY_THRESHOLD)
      .map(e => ({ entity: e._entityName, classification: e._entityType || 'OTHER', url: e._bestURL || '', llmSalience: (e._llmSalience || 0).toFixed(4), yourSalience: (e._yourSalience || 0).toFixed(4), isMatched: e._isMatched }))
      .sort((a, b) => parseFloat(b.llmSalience) - parseFloat(a.llmSalience));

    return { prompt, matchedPct, missingPct, yourAvgSalience: yourAvgSal, llmAvgSalience: llmAvgSal, healthScore, createdAt, entities: { matched: matchedEntities, missing: missingEntities, priority: priorityEntities } };
  };

  const processV48 = (rd) => {
    const details = rd.gaps?.details || {};
    const all = [];
    const push = (arr, isMatched) => (arr || []).forEach(e => {
      if (isCompetitor(e.entity)) return;
      all.push({ _entityName: e.entity, _entityType: (e.type || e.enq_type || 'OTHER').toUpperCase(), _isMatched: isMatched, _yourSalience: e.clusterCoverage || e.matchScore || 0, _llmSalience: e.salience || (e.llmWeight ? e.llmWeight / 100 : 0), _bestURL: e.matchedAs || e.bestURL || e.recommendedUrl || '' });
    });
    push(details.matched, true);
    push(details.weak, true);
    push(details.missing, false);
    return all;
  };

  const processLegacy = (rd) => {
    return (rd?.gapMatrix?.matrix || []).filter(e => !e.is_competitor && !isCompetitor(e.entity)).map(e => ({
      _entityName: e.entity, _entityType: e.entityType || 'OTHER', _isMatched: (e.urlsCovering || 0) > 0,
      _yourSalience: e.avgSupply || 0, _llmSalience: e.promptSalience || 0, _bestURL: e.bestURL || ''
    }));
  };

  const handleSort = (col) => {
    if (sortColumn === col) setSortDirection(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortColumn(col); setSortDirection('desc'); }
  };

  const sorted = [...tableData].sort((a, b) => {
    const av = parseFloat(a[sortColumn]) || 0;
    const bv = parseFloat(b[sortColumn]) || 0;
    return sortDirection === 'asc' ? av - bv : bv - av;
  });

  if (loading) return (
    <div style={{ padding: '48px', textAlign: 'center', fontFamily: "'Work Sans', sans-serif" }}>
      <div style={{ width: '36px', height: '36px', border: '4px solid #e5e7eb', borderTop: `4px solid ${colors.secondary}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
      <p style={{ color: '#6b7280', margin: 0, fontSize: '14px' }}>Loading entity gap data...</p>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={{ padding: '16px', backgroundColor: '#fee2e2', borderRadius: '8px', fontFamily: "'Work Sans', sans-serif" }}>
      <span style={{ color: '#dc2626', fontSize: '14px' }}>{error}</span>
    </div>
  );

  if (tableData.length === 0) return (
    <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '48px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', fontFamily: "'Work Sans', sans-serif" }}>
      <i className="ti ti-topology-star-3" style={{ fontSize: '40px', color: '#d1d5db', marginBottom: '12px', display: 'block' }} />
      <h3 style={{ fontSize: '18px', fontWeight: '600', color: colors.primary, marginBottom: '8px' }}>No entity gap data yet</h3>
      <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Content graph analyses run automatically on schedule.</p>
    </div>
  );

  const SortIcon = ({ col }) => (
    <i className={`ti ${sortColumn === col ? (sortDirection === 'desc' ? 'ti-arrow-down' : 'ti-arrow-up') : 'ti-arrows-sort'}`}
      style={{ fontSize: '12px', marginLeft: '4px', color: sortColumn === col ? colors.secondary : '#d1d5db' }} />
  );

  return (
    <div style={{ fontFamily: "'Work Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '20px 24px', marginBottom: '16px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '700', color: colors.primary, margin: 0, marginBottom: '2px' }}>Entity Gap Analysis</h2>
        <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
          {tableData.length} prompt{tableData.length !== 1 ? 's' : ''} analysed · click a row to see entity detail
        </p>
      </div>

      {/* Table */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ textAlign: 'left', padding: '10px 20px', color: '#6b7280', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Prompt</th>
              {[
                { key: 'matchedPct', icon: 'ti-check', label: 'Matched' },
                { key: 'priorityCount', icon: 'ti-star', label: 'Priority' },
                { key: 'healthScore', icon: 'ti-heart-rate-monitor', label: 'Health' },
              ].map(col => (
                <th key={col.key} onClick={() => handleSort(col.key === 'priorityCount' ? 'matchedPct' : col.key)}
                  style={{ textAlign: 'center', padding: '10px 16px', color: '#6b7280', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <i className={`ti ${col.icon}`} style={{ fontSize: '14px' }} title={col.label} />
                  <SortIcon col={col.key === 'priorityCount' ? 'matchedPct' : col.key} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, idx) => {
              const hb = band(row.healthScore);
              return (
                <tr key={idx}
                  style={{ borderBottom: idx < sorted.length - 1 ? '1px solid #f3f4f6' : 'none', cursor: 'pointer', backgroundColor: 'white' }}
                  onClick={() => setDrawer(row)}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}>
                  <td style={{ padding: '12px 20px', color: colors.primary, lineHeight: '1.4' }}>{row.prompt}</td>
                  <td style={{ textAlign: 'center', padding: '12px 16px' }}>
                    <ScoreCell score={row.matchedPct} />
                  </td>
                  <td style={{ textAlign: 'center', padding: '12px 16px' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '32px', padding: '3px 8px', borderRadius: '6px', backgroundColor: 'rgba(244,162,97,0.12)', color: colors.accent, fontSize: '13px', fontWeight: '600' }}>
                      {row.entities.priority.length}
                    </div>
                  </td>
                  <td style={{ textAlign: 'center', padding: '12px 16px' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '40px', padding: '3px 8px', borderRadius: '6px', backgroundColor: hb.bg, color: hb.text, fontSize: '13px', fontWeight: '600' }}>
                      {row.healthScore}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Legend */}
        <div style={{ padding: '10px 20px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
          {[
            { label: '≥70', bg: 'rgba(1,121,111,0.08)', color: '#01796f' },
            { label: '45–69', bg: 'rgba(233,196,106,0.15)', color: '#854F0B' },
            { label: '<45', bg: 'rgba(231,111,81,0.08)', color: '#993C1D' }
          ].map(({ label, bg, color }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#6b7280' }}>
              <div style={{ width: '20px', height: '12px', borderRadius: '3px', backgroundColor: bg, border: `1px solid ${color}30` }} />
              <span>{label}</span>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#6b7280', marginLeft: '8px' }}>
            <i className="ti ti-star" style={{ fontSize: '12px', color: colors.accent }} /> Priority entities (LLM salience ≥ 0.05)
          </div>
        </div>
      </div>

      {/* Drawer */}
      {drawer && (() => {
        const b = band(drawer.healthScore);
        return (
          <Drawer open={true} onClose={() => setDrawer(null)}
            title={drawer.prompt}
            badge={`${drawer.healthScore} health`}
            badgeBg={b.bg} badgeColor={b.text}>
            <EntityDrawerBody row={drawer} />
          </Drawer>
        );
      })()}
    </div>
  );
}
